import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
// import { PAGINATE_BY } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import InslabelListPage from "@saleor/inslabel/components/InslabelListPage";
import InstagramLabelAddDialog from "@saleor/inslabel/components/InstagramLabelAddDialog";
import {
  useInstagramLabelCreate,
  useInstagramLabelDelete
} from "@saleor/inslabel/mutations";
import { useInstagramLabelList } from "@saleor/inslabel/queries";
import { InstagramLabelDelete } from "@saleor/inslabel/types/InstagramLabelDelete";
import {
  inslabelListUrl,
  InslabelListUrlDialog,
  InslabelListUrlQueryParams,
  inslabelUrl
} from "@saleor/inslabel/urls";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { getSortQueryVariables } from "./sort";

interface InslabelListProps {
  params: InslabelListUrlQueryParams;
}

const InslabelList: React.FC<InslabelListProps> = ({ params }) => {
  const intl = useIntl();

  const paginate = usePaginator();

  const notify = useNotifier();

  const navigate = useNavigator();
  const { updateListSettings, settings } = useListSettings(
    ListViews.INSTAGRAMS_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const queryVariables = {
    ...paginationState,
    sort: getSortQueryVariables(params)
  };

  const { data, loading, refetch } = useInstagramLabelList({
    variables: queryVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.instagramLabels.pageInfo),
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, inslabelListUrl, params);

  const [openModal, closeModal] = createDialogActionHandlers<
    InslabelListUrlDialog,
    InslabelListUrlQueryParams
  >(navigate, inslabelListUrl, params);

  const handleDeleteSuccess = (data: InstagramLabelDelete) => {
    if (data.instagramLabelDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Instagram Label Deleted"
        })
      });
      refetch();
      closeModal();
    }
  };

  const [inslabelDelete] = useInstagramLabelDelete({
    onCompleted: handleDeleteSuccess
  });

  const [addInsLabel] = useInstagramLabelCreate({
    onCompleted: data => {
      if (data.instagramLabelCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added Label"
          })
        });
        navigate(inslabelUrl(data.instagramLabelCreate.label.id), true);
      }
    }
  });

  return (
    <>
      <InslabelListPage
        insLabels={maybe(() =>
          data.instagramLabels.edges.map(edge => edge.node)
        )}
        toatlCount={maybe(() => data.instagramLabels.totalCount, 0)}
        disabled={loading}
        onAdd={() => openModal("add")}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onRowClick={id => () => navigate(inslabelUrl(id))}
        sort={getSortParams(params)}
        onSort={handleSort}
        onDelete={id => openModal("remove", { id })}
        settings={settings}
        onUpdateListSettings={updateListSettings}
      />

      <ActionDialog
        open={params.action === "remove"}
        onClose={closeModal}
        confirmButtonState={"default"}
        onConfirm={() =>
          inslabelDelete({
            variables: {
              id: params.id
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Delete Label",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete this label?"
            description="dialog content"
          />
        </DialogContentText>
      </ActionDialog>

      <InstagramLabelAddDialog
        open={params.action === "add"}
        onClose={closeModal}
        onSubmit={name =>
          addInsLabel({
            variables: {
              input: {
                name
              }
            }
          })
        }
      />
    </>
  );
};
export default InslabelList;
